import { Suspense, lazy, useEffect, useState } from "react";
import {
  HashRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { roomClassFor, FINANZAS_EN_MENU, FINANZAS_GRADOS_EN_MENU, SOLICITUDES_EN_MENU, ALFA_EN_MENU } from "./config";
import { AccessGate, SalaGuard, SubjectDetailGuard, useSinModulos } from "./lib/access";
import { Cargando } from "./components/Cargando";
import { getSession, logout as doLogout } from "./lib/auth";
import { stampLogin } from "./lib/users";
import { flush } from "./lib/analytics";
import type { Session } from "./types";
import { Layout } from "./components/Layout";
import { Login } from "./pages/Login";
import { RoomPage } from "./pages/RoomPage";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { KioscoIdle } from "./components/KioscoIdle";
import { useRegistroDeSalas } from "./lib/usoDeSalas";
import { sincronizarProgreso } from "./lib/learningProgress";


/*
 * División por ruta. Cada una de estas pantallas se descarga cuando se entra en
 * ella, no al abrir la app.
 *
 * Antes todo viajaba en un solo bundle: un estudiante que entra a Elemental
 * descargaba el panel de administración, los diez juegos y la videoteca sin
 * llegar a verlos nunca. `Login` y `RoomPage` quedan estáticos a propósito —
 * son la primera pantalla real de cada rol y cargarlas en diferido solo añadiría
 * un parpadeo.
 */
const SubjectDetail = lazy(() => import("./pages/SubjectDetail").then((m) => ({ default: m.SubjectDetail })));
const Servicios = lazy(() => import("./pages/Servicios").then((m) => ({ default: m.Servicios })));
const AgencyDetail = lazy(() => import("./pages/AgencyDetail").then((m) => ({ default: m.AgencyDetail })));
const ComingSoon = lazy(() => import("./pages/ComingSoon").then((m) => ({ default: m.ComingSoon })));
const Bibliotecaria = lazy(() => import("./pages/Bibliotecaria").then((m) => ({ default: m.Bibliotecaria })));
const Adultos = lazy(() => import("./pages/Adultos").then((m) => ({ default: m.Adultos })));
const Universidad = lazy(() => import("./pages/Universidad").then((m) => ({ default: m.Universidad })));
const UniversidadDetalle = lazy(() => import("./pages/UniversidadDetalle").then((m) => ({ default: m.UniversidadDetalle })));
const InstitutoDetalle = lazy(() => import("./pages/InstitutoDetalle").then((m) => ({ default: m.InstitutoDetalle })));
// StemRoom incluye la rejilla de Juegos (los juegos viven en su chunk vía este
// import diferido). La ruta /juegos ahora redirige a /stem: son una sola página.
const StemRoom = lazy(() => import("./pages/StemRoom").then((m) => ({ default: m.StemRoom })));
// Recursos ahora vive DENTRO de STEM y Juegos (StemRoom lo embebe). /recursos redirige.
const Videoteca = lazy(() => import("./pages/Videoteca").then((m) => ({ default: m.Videoteca })));
const VideotecaDeportes = lazy(() => import("./pages/VideotecaDeportes").then((m) => ({ default: m.VideotecaDeportes })));
const IA = lazy(() => import("./pages/IA").then((m) => ({ default: m.IA })));
const Refuerzo = lazy(() => import("./pages/Refuerzo").then((m) => ({ default: m.Refuerzo })));
const Eventos = lazy(() => import("./pages/Eventos").then((m) => ({ default: m.Eventos })));
const Perfil = lazy(() => import("./pages/Perfil").then((m) => ({ default: m.Perfil })));
const Alfabetizacion = lazy(() => import("./pages/Alfabetizacion").then((m) => ({ default: m.Alfabetizacion })));
const Finanzas = lazy(() => import("./pages/Finanzas").then((m) => ({ default: m.Finanzas })));
const Empleo = lazy(() => import("./pages/Empleo").then((m) => ({ default: m.Empleo })));

function Protected({ session, onLogout }: { session: Session; onLogout: () => void }) {
  const { pathname } = useLocation();
  // Visitas por sala para el panel: kiosco y participantes. El personal no, que
  // revisa pantallas y no las usa.
  const registra = session.role === "kiosk" || session.cognitoRole === "participant";
  useRegistroDeSalas(pathname, registra, session.role === "kiosk" ? "kiosco" : "participante");
  // El progreso sigue a la persona: al entrar se trae lo que avanzó en otro equipo.
  useEffect(() => {
    if (session.cognitoRole === "participant") void sincronizarProgreso();
  }, [session.cognitoRole]);
  /*
   * La sala manda en TODA su rama, no solo en su índice. Antes esta clase la
   * ponía RoomPage en su propio div, así que Nunito y Baloo 2 se perdían en
   * cuanto se abría una materia o una lección — justo donde el niño se queda.
   */
  return (
    // AccessGate carga el acceso por sala del participante (fail-closed) y lo provee por
    // contexto a Layout (nav) y a las guardas de ruta. Para staff es transparente.
    <AccessGate session={session}>
      <Layout session={session} onLogout={onLogout}>
        <Vista pathname={pathname} />
      </Layout>
      {/*
       * Solo para el kiosco: es una cuenta compartida entre quien llega a la
       * biblioteca sin registrarse, y el único rol donde "nadie lo toca" tiene
       * que traducirse en una pantalla en blanco, no en una sesión cerrada.
       * `Protected` es el elemento de la ruta de layout (envuelve el `Outlet`
       * de toda la rama autenticada), así que se monta una sola vez por sesión
       * y sigue vivo al navegar entre salas — justo lo que necesita un
       * temporizador que no debe reiniciarse solo porque cambió la ruta.
       */}
      {/* `destino` sale de `home(session)`, la misma función que decide a dónde
          entra el kiosco al iniciar sesión: una sola fuente de verdad para
          "a dónde va el kiosco", en vez de escribir "/servicios" a mano aquí
          también (ver el comentario de `KioscoIdle`). */}
      {session.role === "kiosk" && <KioscoIdle minutos={5} destino={home(session)} />}
    </AccessGate>
  );
}

/**
 * Contenido de la ruta. Es un componente aparte —y no el cuerpo de `Protected`—
 * porque `useSinModulos` necesita estar DEBAJO de `AccessGate` en el árbol, y
 * `Protected` renderiza esa compuerta en su propio return.
 */
function Vista({ pathname }: { pathname: string }) {
  const sinModulos = useSinModulos();
  return (
    /* `vista` es el contenedor de la ruta. Lleva nombre propio —además de la
       clase de sala— porque el modo lección necesita un asidero para pasarle el
       alto: sin él, este div se queda del tamaño de su contenido y corta la
       cadena de flex entre `.main` y el reproductor. */
    <div className={["vista", roomClassFor(pathname)].filter(Boolean).join(" ")}>
      {sinModulos ? (
        <div className="center-msg">
          {/* h1: es lo único que hay en la pantalla y sin él no había titular. */}
          <h1>Tu cuenta no tiene módulos activos</h1>
          <p>
            Puede ser que tu grado esté apagado en este centro o que todavía no te hayan
            asignado los módulos. Habla con la bibliotecaria para que revise tu cuenta.
          </p>
        </div>
      ) : (
        <Suspense fallback={<Cargando />}>
          <Outlet />
        </Suspense>
      )}
    </div>
  );
}

/*
 * Exportada para poder probarla sin montar la aplicación entera (ver
 * test/kiosco.ramas.test.tsx). Antes era una función interna: una de las tres
 * ramas mudas del kiosco vivía aquí sin forma de comprobarla.
 */
export function home(session: Session): string {
  // El kiosco entra por Servicios: es a lo que viene la mayoría de quien llega al
  // mostrador sin cuenta. Sin esta rama iría al PANEL.
  if (session.role === "kiosk") return "/servicios";
  if (session.role === "student") return "/elemental";
  return session.cognitoRole === "librarian" ? "/admin/visitas" : "/admin/dashboard";
}

// Compuerta de Educación Financiera: con el flag apagado, los participantes ven
// ComingSoon (contenido sin revisar no se publica); staff y demo entran en modo revisión.
function finanzasElement(session: Session) {
  const esParticipante = session.cognitoRole === "participant";
  if (!FINANZAS_EN_MENU && esParticipante) {
    return (
      <ComingSoon
        room="adultos"
        kicker="Para todos"
        title="Educación Financiera"
        subtitle="Ahorro, crédito, cuentas de banco y presupuesto — paso a paso."
        note="Estará disponible muy pronto."
      />
    );
  }
  // El selector de grados se muestra a todos si el flag está on, o al staff/demo (revisión).
  const grados = FINANZAS_GRADOS_EN_MENU || esParticipante === false;
  return <Finanzas review={!FINANZAS_EN_MENU} grados={grados} />;
}

/*
 * El árbol de rutas de una sesión YA AUTENTICADA. Extraído de `Shell` para que
 * se pueda renderizar aparte en pruebas (`test/kiosco.ramas.test.tsx`) sin
 * montar el login ni el resto de la aplicación — sin esto no hay forma de
 * probar el rebote de /perfil del kiosco de verdad, solo de confiar en que
 * alguien lo revise a ojo. Es un movimiento de código: el árbol de rutas es
 * el mismo, solo cambia de función.
 *
 * `onLogout` es OBLIGATORIO, no opcional con no-op por defecto: un no-op
 * silencioso convertiría "me olvidé de pasarlo" en un botón "Salir" que no
 * hace nada, sin que el compilador se queje. Las pruebas que no necesitan un
 * cierre de sesión real (las de la rama del kiosco) pasan `() => {}` a
 * propósito, a la vista de quien lea la prueba, no por un valor por defecto.
 */
export function AppRoutes({ session, onLogout }: { session: Session; onLogout: () => void }) {
  return (
    <Routes>
      <Route element={<Protected session={session} onLogout={onLogout} />}>
        <Route path="/elemental" element={<SalaGuard sala="elemental"><RoomPage room="elemental" /></SalaGuard>} />
        <Route path="/intermedia" element={<SalaGuard sala="intermedia"><RoomPage room="intermedia" /></SalaGuard>} />
        <Route path="/superior" element={<SalaGuard sala="superior"><RoomPage room="superior" /></SalaGuard>} />
        <Route path="/stem" element={<SalaGuard sala="stem"><StemRoom /></SalaGuard>} />
        {/* /juegos se fundió con /stem (STEM y Juegos). Redirige para no romper enlaces. */}
        <Route path="/juegos" element={<Navigate to="/stem" replace />} />
        <Route path="/ia" element={<SalaGuard sala="ia"><IA /></SalaGuard>} />
        <Route path="/recursos" element={<Navigate to="/stem" replace />} />
        <Route path="/videoteca" element={<SalaGuard sala="videoteca"><Videoteca /></SalaGuard>} />
        <Route path="/deportes" element={<SalaGuard sala="deportes"><VideotecaDeportes /></SalaGuard>} />
        <Route path="/eventos" element={<SalaGuard sala="eventos"><Eventos /></SalaGuard>} />
        <Route path="/universidad" element={<SalaGuard sala="universidad"><Universidad /></SalaGuard>} />
        {/* Detalle por universidad (programas por área). Antes del catch-all
            /:room/:slug, que si no lo trataría como una sala. */}
        {/* Detalle de un instituto de carreras cortas. Tres segmentos, así que ni
            /universidad/:slug ni /:room/:slug casan con ella; va antes igual para
            que quien lea las rutas de arriba abajo la encuentre junto a su sala. */}
        <Route path="/universidad/cortas/:slug" element={<SalaGuard sala="universidad"><InstitutoDetalle /></SalaGuard>} />
        <Route path="/universidad/:slug" element={<SalaGuard sala="universidad"><UniversidadDetalle /></SalaGuard>} />
        <Route path="/servicios" element={<SalaGuard sala="servicios"><Servicios /></SalaGuard>} />
        {/* Antes de /:room/:slug: más específica gana igual en React Router,
            pero declararla aquí evita que alguien la mueva y la rompa. */}
        <Route path="/servicios/:slug" element={<SalaGuard sala="servicios"><AgencyDetail /></SalaGuard>} />
        {/* Antes del comodín /:room/:slug, que si no se tragaría esta ruta. */}
        <Route path="/refuerzo" element={<SalaGuard sala="refuerzo"><Refuerzo /></SalaGuard>} />
        <Route path="/:room/:slug" element={<SubjectDetailGuard><SubjectDetail /></SubjectDetailGuard>} />
        <Route
          path="/tareas"
          element={
            <ComingSoon
              room="tareas"
              kicker="Próximamente"
              title="Centro de Tareas"
              subtitle="Apoyo para tareas, prácticas imprimibles y repaso guiado."
              note="La bibliotecaria puede encenderlo en el portal para mostrar cómo se habilitarían módulos."
            />
          }
        />
        {/* Solicitudes apagada (ver SOLICITUDES_EN_MENU): la URL vieja vuelve al inicio. */}
        <Route path="/adultos" element={SOLICITUDES_EN_MENU ? <SalaGuard sala="adultos"><Adultos /></SalaGuard> : <Navigate to={home(session)} replace />} />
        <Route path="/finanzas" element={<SalaGuard sala="finanzas">{finanzasElement(session)}</SalaGuard>} />
        <Route path="/empleo" element={<SalaGuard sala="empleo"><Empleo /></SalaGuard>} />
        {/* El kiosco no tiene perfil: es una cuenta compartida y anónima. */}
        <Route path="/perfil" element={session.role === "kiosk" ? <Navigate to={home(session)} replace /> : <Perfil />} />
        {/* Sin ALFA_EN_MENU la unidad no tiene sus audios: solo el personal la abre
            por URL para revisarla; un participante o el kiosco vuelve al inicio. */}
        <Route
          path="/alfabetizacion"
          element={!ALFA_EN_MENU && (session.role === "kiosk" || session.cognitoRole === "participant")
            ? <Navigate to={home(session)} replace />
            : <SalaGuard sala="alfabetizacion"><Alfabetizacion /></SalaGuard>}
        />
        <Route path="/admin" element={<Navigate to={home(session)} replace />} />
        {/*
         * El panel es de personal. Sin esta guarda, un kiosco que teclee
         * "#/admin/usuarios" monta Bibliotecaria de verdad, y sus efectos
         * disparan GET /users y GET /tickets — ninguna de las dos está en la
         * lista blanca del backend, así que son dos 403 por intento, desde una
         * pantalla pública. Y como /admin/:section no tiene salida propia
         * (no es una SalaGuard que rebote a una sala), la sesión queda
         * encallada ahí con el área principal en blanco, en vez de terminar
         * donde sí tiene algo que ver.
         */}
        <Route path="/admin/:section" element={session.role === "kiosk" ? <Navigate to={home(session)} replace /> : <Bibliotecaria />} />
        {/* Ficha de un usuario. Va en la URL y no en un modal a propósito: es un
            enlace que se comparte ("mira la ficha de este participante") y el
            botón atrás del navegador hace lo que uno espera. Misma guarda que
            arriba: la URL con :id es igual de alcanzable tecleándola. */}
        <Route path="/admin/:section/:id" element={session.role === "kiosk" ? <Navigate to={home(session)} replace /> : <Bibliotecaria />} />
        <Route path="/bibliotecaria" element={<Navigate to={home(session)} replace />} />
        <Route path="*" element={<Navigate to={home(session)} replace />} />
      </Route>
    </Routes>
  );
}

function Shell() {
  const [session, setSession] = useState<Session | null>(() => getSession());
  const navigate = useNavigate();

  // Envía la analítica acumulada al arrancar (si hay red).
  useEffect(() => {
    void flush();
  }, []);

  function handleLogout() {
    doLogout();
    setSession(null);
    navigate("/login");
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          session ? (
            <Navigate to={home(session)} replace />
          ) : (
            <Login
              onLogin={(s) => {
                setSession(s);
                // El kiosco no sella último acceso: no es una persona, y la ruta
                // está fuera de su lista blanca. Llamarla dejaría un 403 esperado
                // en cada apertura del centro.
                if (s.role !== "kiosk") stampLogin();
                navigate(home(s));
              }}
            />
          )
        }
      />
      {/*
       * El resto del árbol vive en `AppRoutes` (ver arriba). `/*` para que sus
       * rutas anidadas casen bajo esta — es el patrón normal de React Router
       * para componer un <Routes> dentro de otro.
       */}
      <Route
        path="/*"
        element={session ? <AppRoutes session={session} onLogout={handleLogout} /> : <Navigate to="/login" replace />}
      />
    </Routes>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <HashRouter>
        <Shell />
      </HashRouter>
    </ErrorBoundary>
  );
}
